<template>
  <section class="flex flex-col gap-4">
    <div>
      <h3 class="text-text-primary text-2xl font-semibold">{{ $t('play.lan_party.live_title') }}</h3>
      <p class="text-text-secondary text-sm mt-1 max-w-2xl">{{ $t('play.lan_party.live_intro') }}</p>
    </div>
    <p v-if="loaded && cards.length === 0" class="text-text-secondary">{{ $t('play.lan_party.lan_parties_empty') }}</p>
    <div v-else class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      <div
        v-for="card in cards"
        :key="card.creator.slug"
        class="bg-gray-900 border rounded-lg p-5 flex flex-col gap-4"
        :class="card.mine || card.joined ? 'border-primary-800' : 'border-gray-800'"
      >
        <div class="flex items-center gap-3">
          <span class="relative flex-shrink-0">
            <span class="block rounded-full p-[2px]" :style="{ background: card.creator.twitch?.live ? '#E91916' : '#374151' }">
              <LanAvatar :src="card.creator.picture_url" size="w-12 h-12 text-lg border-2 border-gray-900" />
            </span>
            <span
              v-if="card.creator.twitch?.live"
              class="absolute -bottom-1 left-1/2 -translate-x-1/2 text-[9px] font-bold text-white px-1 rounded"
              style="background:#E91916"
            >{{ $t('play.lan_party.live') }}</span>
          </span>
          <div class="min-w-0 flex-1">
            <p class="font-semibold text-text-primary flex items-center gap-1.5">{{ card.creator.name }}</p>
            <p class="text-xs flex items-center gap-1 min-w-0" style="color:#BF94FF">
              <a
                v-if="twitchLogin(card.creator.channel_url)"
                :href="safeHttpUrl(card.creator.channel_url)"
                target="_blank"
                rel="noopener"
                class="inline-flex items-center gap-1 hover:underline"
              ><TwitchIcon class="w-3.5 h-3.5" />{{ twitchLogin(card.creator.channel_url) }}</a>
              <TwitchViewers
                v-if="card.creator.twitch?.live"
                class="ml-1"
                :count="card.creator.twitch.viewer_count"
              />
            </p>
          </div>
          <span
            v-if="card.lan"
            class="inline-flex items-center gap-1.5 text-[11px] font-bold tracking-wide px-1.5 py-0.5 rounded"
            style="background:#EF444420;color:#EF6B6B"
          ><span class="w-1.5 h-1.5 rounded-full bg-red-500" aria-hidden="true" />{{ $t('play.lan_party.lan_on') }}</span>
        </div>
        <div v-if="card.lan" class="flex flex-wrap items-center gap-2 text-xs">
          <LanChips :lan-party="card.lan" />
        </div>
        <div v-if="card.lan" class="flex flex-col gap-2">
          <p class="text-xs uppercase tracking-widest text-gray-400 font-bold">{{ $t('play.lan_party.players_n', { n: card.lan.member_count }) }}</p>
          <div v-if="card.lan.members?.length" class="flex">
            <LanAvatar
              v-for="(member, i) in card.lan.members"
              :key="member.uuid"
              :src="member.avatar"
              :title="member.username"
              size="w-8 h-8 text-[10px] border-2 border-gray-900"
              :class="i > 0 && '-ml-1.5'"
            />
            <span
              v-if="card.lan.member_count > card.lan.members.length"
              class="-ml-1.5 w-8 h-8 rounded-full bg-gray-800 border-2 border-gray-900 text-[10px] font-semibold text-text-secondary flex items-center justify-center"
            >+{{ card.lan.member_count - card.lan.members.length }}</span>
          </div>
        </div>
        <p v-else class="text-sm text-text-secondary">{{ $t('play.lan_party.no_night_yet') }}</p>
        <div class="flex items-center justify-end gap-3">
          <span
            v-if="card.joined"
            class="mr-auto inline-flex items-center gap-1.5 text-sm font-semibold text-primary-300"
          ><CheckCircleIcon class="w-4 h-4" aria-hidden="true" />{{ $t('play.lan_party.you_are_in') }}</span>
          <router-link
            v-if="card.mine && card.lan"
            :to="{ name: 'LanPartyPage', params: { slug: card.creator.slug } }"
            class="px-4 py-2.5 rounded-lg bg-gray-800 hover:bg-gray-700 text-text-primary text-sm font-semibold"
          >{{ $t('play.lan_party.viewer_side') }}</router-link>
          <router-link
            v-if="card.mine"
            :to="{ name: 'LanControlRoomPage', params: { slug: card.creator.slug } }"
            class="px-4 py-2.5 rounded-lg bg-primary-600 hover:bg-primary-500 text-white text-sm font-semibold"
          >{{ $t('play.lan_party.control_room') }}</router-link>
          <router-link
            v-else
            :to="{ name: 'LanPartyPage', params: { slug: card.creator.slug } }"
            class="px-4 py-2.5 rounded-lg bg-primary-600 hover:bg-primary-500 text-white text-sm font-semibold"
          >{{ card.joined ? $t('play.lan_party.back_to_lan') : $t('play.lan_party.join') }}</router-link>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, onBeforeUnmount } from 'vue';
import axios from 'axios';
import { useStore } from 'vuex';
import { CheckCircleIcon } from '@heroicons/vue/24/solid';
import LanAvatar from './LanAvatar.vue';
import LanChips from './LanChips.vue';
import TwitchIcon from './TwitchIcon.vue';
import TwitchViewers from './TwitchViewers.vue';
import { safeHttpUrl, twitchLogin } from '../../utils/lan_party';

const store = useStore();
const lanParties = ref([]);
const loaded = ref(false);

// The brand I manage always has a card, live or not; it is the way into the control room.
const cards = computed(() => {
  const mine = store.getters.matchmakingContext?.creator;
  const joinedUuid = store.getters.matchmakingContext?.lan_party?.uuid;
  const others = lanParties.value
    .filter((lan) => lan.creator.slug !== mine?.slug)
    .map((lan) => ({
      creator: lan.creator, lan, mine: false, joined: !!joinedUuid && lan.uuid === joinedUuid,
    }));
  const myLan = mine && (lanParties.value.find((lan) => lan.creator.slug === mine.slug) || null);
  const all = mine ? [{ creator: mine, lan: myLan, mine: true }, ...others] : others;
  // Live first, then by name.
  return all.sort((a, b) => (!!b.lan - !!a.lan) || a.creator.name.localeCompare(b.creator.name));
});

function load() {
  axios.get('/matchmaking/lan_parties').then(({ data }) => {
    lanParties.value = data.lan_parties;
    loaded.value = true;
  }).catch(() => {});
}

load();
const timer = setInterval(load, 30_000);
onBeforeUnmount(() => clearInterval(timer));
</script>
